import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getParameter, createParameter, updateParameter, listParameters } from '../services/parameterService';
import { convertFormula } from '../services/agreementService';
import type { Parameter } from '../types';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { TextArea } from '../components/ui/TextArea';
import { Alert } from '../components/ui/Alert';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import FormulaTextArea from '../components/ui/FormulaTextArea';
import ParameterTestDialog from './ParameterTestDialog';

const dataTypeOptions = [
  { value: '', label: 'Select data type' },
  { value: 'TEXT', label: 'Text' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'DATE', label: 'Date' },
  { value: 'DROPDOWN', label: 'Dropdown' },
];

const parameterTypeOptions = [
  { value: '', label: 'Select parameter type' },
  { value: 'INPUT', label: 'Input' },
  { value: 'CALCULATED', label: 'Calculated' },
];

const sourceTypeOptions = [
  { value: '', label: 'Select source type' },
  { value: 'MANUAL', label: 'Manual (Operator Input)' },
  { value: 'SYSTEM', label: 'System (ATS Variable)' },
];

export default function ParameterFormPage() {
  const navigate = useNavigate();
  const { parameterId } = useParams<{ parameterId: string }>();
  const isEditMode = Boolean(parameterId);

  const [parameterType, setParameterType] = useState<'INPUT' | 'CALCULATED' | ''>('');
  const [parameterName, setParameterName] = useState('');
  const [dataType, setDataType] = useState('');
  const [sourceType, setSourceType] = useState<'MANUAL' | 'SYSTEM' | ''>('MANUAL');
  const [displayOrder, setDisplayOrder] = useState('0');
  const [naturalLanguageFormula, setNaturalLanguageFormula] = useState('');
  const [executableFormula, setExecutableFormula] = useState('');
  const [dropdownValuesText, setDropdownValuesText] = useState('');
  const [validationMin, setValidationMin] = useState('');
  const [validationMax, setValidationMax] = useState('');
  const [validationRegex, setValidationRegex] = useState('');
  const [validationRequired, setValidationRequired] = useState(false);

  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [availableVariables, setAvailableVariables] = useState<string[]>([]);
  const [isConverting, setIsConverting] = useState(false);
  const [convertError, setConvertError] = useState<string | null>(null);

  useEffect(() => {
    // Fetch all parameter names for formula autocomplete
    listParameters({ limit: 1000 })
      .then((res) => {
        setAvailableVariables(res.parameters.map((p) => p.parameterName));
      })
      .catch(() => { /* ignore */ });
  }, []);

  useEffect(() => {
    if (isEditMode && parameterId) {
      setIsLoadingData(true);
      getParameter(Number(parameterId))
        .then((param: Parameter) => {
          setParameterType(param.parameterType);
          setParameterName(param.parameterName);
          setDataType(param.dataType || '');
          // Treat empty/null source_type as SYSTEM for INPUT parameters
          setSourceType(param.sourceType || (param.parameterType === 'INPUT' ? 'SYSTEM' : 'MANUAL'));
          setDisplayOrder(String(param.displayOrder));
          setNaturalLanguageFormula(param.naturalLanguageFormula || '');
          setExecutableFormula(param.executableFormula || '');
          if (param.dropdownValues?.length) {
            setDropdownValuesText(param.dropdownValues.join('\n'));
          }
          if (param.validationRules) {
            if (param.validationRules.min !== undefined) setValidationMin(String(param.validationRules.min));
            if (param.validationRules.max !== undefined) setValidationMax(String(param.validationRules.max));
            if (param.validationRules.regex) setValidationRegex(param.validationRules.regex);
            if (param.validationRules.required) setValidationRequired(true);
          }
        })
        .catch(() => setError('Failed to load parameter data.'))
        .finally(() => setIsLoadingData(false));
    }
  }, [isEditMode, parameterId]);

  function validate(): boolean {
    const errors: Record<string, string> = {};
    if (!parameterName.trim()) errors.parameterName = 'Parameter name is required';
    else if (/\s/.test(parameterName.trim())) errors.parameterName = 'Parameter name must not contain spaces. Use underscores or camelCase (e.g., AnaesthesiaCharges)';
    if (!parameterType) errors.parameterType = 'Parameter type is required';
    if (parameterType === 'INPUT' && !dataType) errors.dataType = 'Data type is required for input parameters';
    if (parameterType === 'INPUT' && dataType === 'DROPDOWN' && !dropdownValuesText.trim()) {
      errors.dropdownValues = 'Dropdown values are required';
    }
    if (parameterType === 'CALCULATED' && !naturalLanguageFormula.trim()) {
      errors.naturalLanguageFormula = 'Formula is required for calculated parameters';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const validationRules: Record<string, unknown> = {};
      if (parameterType === 'INPUT') {
        if (validationRequired) validationRules.required = true;
        if (dataType === 'NUMBER') {
          if (validationMin) validationRules.min = Number(validationMin);
          if (validationMax) validationRules.max = Number(validationMax);
        }
        if (dataType === 'TEXT' && validationRegex) {
          validationRules.regex = validationRegex;
        }
      }

      const dropdownValues = parameterType === 'INPUT' && dataType === 'DROPDOWN'
        ? dropdownValuesText.split('\n').map(v => v.trim()).filter(Boolean)
        : undefined;

      if (isEditMode && parameterId) {
        const result = await updateParameter(Number(parameterId), {
          parameterName: parameterName.trim(),
          dataType: parameterType === 'INPUT' ? dataType : undefined,
          sourceType: parameterType === 'INPUT' ? sourceType : undefined,
          naturalLanguageFormula: parameterType === 'CALCULATED' ? naturalLanguageFormula : undefined,
          validationRules: parameterType === 'INPUT' ? validationRules : undefined,
          dropdownValues,
          displayOrder: Number(displayOrder),
        });
        if (result.executableFormula) setExecutableFormula(result.executableFormula);
        setSuccess('Parameter updated successfully.');
        setTimeout(() => navigate('/parameters'), 1500);
      } else {
        const result = await createParameter({
          parameterName: parameterName.trim(),
          parameterType: parameterType as 'INPUT' | 'CALCULATED',
          dataType: parameterType === 'INPUT' ? dataType : undefined,
          sourceType: parameterType === 'INPUT' ? sourceType : undefined,
          naturalLanguageFormula: parameterType === 'CALCULATED' ? naturalLanguageFormula : undefined,
          validationRules: parameterType === 'INPUT' ? validationRules : undefined,
          dropdownValues,
          displayOrder: Number(displayOrder),
        });
        if (result.executableFormula) setExecutableFormula(result.executableFormula);
        setSuccess(`Parameter "${result.parameterName}" created successfully.`);
        setTimeout(() => navigate('/parameters'), 2000);
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { error?: string } } };
      const status = axiosErr.response?.status;
      const message = axiosErr.response?.data?.error;

      if (status === 409) {
        setError('Parameter name already exists. Please use a different name.');
      } else if (status === 422) {
        setError(message || 'Formula conversion failed. Please rephrase your formula.');
      } else if (status === 400) {
        setError(message || 'Validation error. Please check your input.');
      } else {
        setError(message || 'An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isLoadingData) {
    return <LoadingSpinner message="Loading parameter data..." />;
  }

  return (
    <div className="parameter-form-page">
      <h2>{isEditMode ? 'Edit Parameter' : 'Create Parameter'}</h2>

      {error && (
        <Alert type="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert type="success" onDismiss={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <form onSubmit={handleSubmit} noValidate>
        {!isEditMode && (
          <Select
            label="Parameter Type"
            options={parameterTypeOptions}
            value={parameterType}
            onChange={(e) => {
              const val = e.target.value as 'INPUT' | 'CALCULATED' | '';
              setParameterType(val);
              if (val === 'INPUT') setValidationRequired(true);
            }}
            error={fieldErrors.parameterType}
          />
        )}

        <Input
          label="Parameter Name"
          value={parameterName}
          onChange={(e) => setParameterName(e.target.value)}
          error={fieldErrors.parameterName}
          required
          placeholder="Enter parameter name"
        />

        <Input
          label="Display Order"
          type="number"
          value={displayOrder}
          onChange={(e) => setDisplayOrder(e.target.value)}
          placeholder="0"
        />

        {/* INPUT parameter fields */}
        {parameterType === 'INPUT' && (
          <>
            <Select
              label="Data Type"
              options={dataTypeOptions}
              value={dataType}
              onChange={(e) => setDataType(e.target.value)}
              error={fieldErrors.dataType}
            />

            <Select
              label="Source Type"
              options={sourceTypeOptions}
              value={sourceType}
              onChange={(e) => setSourceType(e.target.value as 'MANUAL' | 'SYSTEM' | '')}
            />
            <p className="field-helper">MANUAL: Operator enters value. SYSTEM: Value from ATS table.</p>

            {dataType === 'DROPDOWN' && (
              <TextArea
                label="Dropdown Values (one per line)"
                value={dropdownValuesText}
                onChange={(e) => setDropdownValuesText(e.target.value)}
                error={fieldErrors.dropdownValues}
                rows={4}
                placeholder="Option 1&#10;Option 2&#10;Option 3"
              />
            )}

            <div className="form-field">
              <label>
                <input
                  type="checkbox"
                  checked={validationRequired}
                  onChange={(e) => setValidationRequired(e.target.checked)}
                />{' '}
                Required
              </label>
            </div>

            {dataType === 'NUMBER' && (
              <div className="validation-rules-row">
                <Input
                  label="Min Value"
                  type="number"
                  value={validationMin}
                  onChange={(e) => setValidationMin(e.target.value)}
                  placeholder="Minimum"
                />
                <Input
                  label="Max Value"
                  type="number"
                  value={validationMax}
                  onChange={(e) => setValidationMax(e.target.value)}
                  placeholder="Maximum"
                />
              </div>
            )}

            {dataType === 'TEXT' && (
              <Input
                label="Regex Pattern"
                value={validationRegex}
                onChange={(e) => setValidationRegex(e.target.value)}
                placeholder="e.g., ^[A-Z]{3}$"
                helperText="Optional regex pattern for text validation"
              />
            )}
          </>
        )}

        {/* CALCULATED parameter fields */}
        {parameterType === 'CALCULATED' && (
          <>
            <FormulaTextArea
              label="Natural Language Formula"
              value={naturalLanguageFormula}
              onChange={(e) => setNaturalLanguageFormula(e.target.value)}
              error={fieldErrors.naturalLanguageFormula}
              rows={4}
              placeholder="e.g., if @Gross_Bill is greater than 10000 then @Gross_Bill * 0.15 (type @ to see variables)"
              variables={availableVariables}
            />

            <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={async () => {
                  if (!naturalLanguageFormula.trim()) return;
                  setIsConverting(true);
                  setConvertError(null);
                  try {
                    const result = await convertFormula({
                      formula: naturalLanguageFormula,
                      inputParameterNames: availableVariables,
                    });
                    setExecutableFormula(result.executableFormula);
                  } catch (err: any) {
                    setConvertError(err?.response?.data?.error || err?.message || 'Formula conversion failed.');
                  } finally {
                    setIsConverting(false);
                  }
                }}
                disabled={!naturalLanguageFormula.trim() || isConverting}
                isLoading={isConverting}
              >
                Convert to Formula
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setShowTestDialog(true)}
                disabled={!naturalLanguageFormula.trim()}
              >
                Test Formula
              </Button>
            </div>

            {convertError && (
              <div style={{ marginTop: '0.5rem' }}>
                <Alert type="error" onDismiss={() => setConvertError(null)}>
                  {convertError}
                </Alert>
              </div>
            )}

            <FormulaTextArea
              label="Executable Formula"
              value={executableFormula}
              onChange={(e) => setExecutableFormula(e.target.value)}
              rows={3}
              placeholder="Generated executable formula will appear here (or type manually with @ for variables)"
              variables={availableVariables}
            />
          </>
        )}

        <div className="form-actions">
          <Button type="submit" isLoading={isSubmitting} disabled={isSubmitting}>
            {isEditMode ? 'Update Parameter' : 'Create Parameter'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/parameters')}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        </div>
      </form>

      {showTestDialog && (
        <ParameterTestDialog
          isOpen={showTestDialog}
          onClose={() => setShowTestDialog(false)}
          formula={naturalLanguageFormula}
        />
      )}
    </div>
  );
}
